/**
 * Live Options Flow — filter contract.
 *
 * ONE source of truth shared by:
 *  - the page URL  (?ticker=NVDA&min_premium=100000&type=call ...)
 *  - the REST screener call  (options_flow_trades filter body)
 *  - the live SSE stream  (query string the PKA hub predicate reads)
 *  - agent citation links  (batch params → same URL)
 *
 * The URL param names ARE our screener field names, so converting an agent
 * `batch([{name:'options_flow_trades', ticker:'NVDA', min_premium:100000}])`
 * call into a shareable page URL is a dumb string mapping with no translation.
 */

// Numeric fields that support min_<f> / max_<f> range params.
export const FLOW_NUMERIC_FIELDS = [
  "premium",
  "size",
  "price",
  "underlying_price",
  "strike",
  "dte",
  "delta",
  "iv",
  "gamma",
  "theta",
  "volume",
  "open_interest",
  "diff",
  "vol_oi_ratio",
  "spread",
  "ask_perc",
  "bid_perc",
  "skew",
  "earnings_dte",
  "marketcap",
] as const;

// Boolean flag fields. URL value "true" → show only rows with the flag;
// "false" → hide rows with the flag (UW's Show/Hide semantics).
export const FLOW_BOOL_FIELDS = [
  "is_sweep",
  "is_floor",
  "is_cross",
  "is_multi_leg",
  "is_single_leg",
  "is_otm",
  "opening",
  "vol_greater_oi",
  "size_greater_oi",
  "exclude_deep_itm",
  "opex_only",
  "intraday_only",
  "hide_expired",
  "unusual",
] as const;

// Multi-select (comma-separated) fields → the screener field they filter, using
// the in_range op (which the screener treats as "value in list").
export const FLOW_MULTI_FIELDS: Record<string, string> = {
  issue_types: "issue_type",
  sectors: "sector",
  industries: "industry",
  tags: "tags",
  excluded_tags: "excluded_tags",
  trade_codes: "trade_codes",
  exchanges: "exchange",
  expiry_dates: "expiry_dates",
};

// Single-value equality fields.
export const FLOW_EQ_FIELDS = ["ticker", "type", "side", "sentiment"] as const;

// Params the browser evaluates itself on every row (REST + live) because they
// can't be expressed as simple screener filters. See applyClientFilters().
export const FLOW_CLIENT_FIELDS = [
  "flag_types", // allow-list over sweep|cross|floor|normal
  "sides", // allow-list over ask|bid|mid|none — client-side so "none" works
  "min_earnings_gap", // days between contract expiry and next earnings
  "max_earnings_gap",
  "avoid_earnings", // true → contract expires before next earnings (or none)
  "earnings_week", // true → earnings within 7 days
  "show_canceled", // true → include canceled/modified prints (live rows)
  "aggregated", // true → collapse identical prints into one row (display only)
  "min_time", // "HH:MM" Eastern, today
  "max_time",
] as const;

// Every field the tape can sort by. Verified live (2026-07-03): the UW API
// only truly honors timestamp ordering — every other order_by is ignored
// upstream, and the screener sorts the fetched page locally in pandas. So any
// selected column can sort; these are the ones we expose.
export const FLOW_ORDER_ALLOWED = [
  "timestamp",
  "premium",
  "size",
  "price",
  "underlying_price",
  "volume",
  "open_interest",
  "strike",
  "dte",
  "expiry",
  "delta",
  "gamma",
  "theta",
  "vega",
  "rho",
  "theo",
  "iv",
  "spread",
  "diff",
  "vol_oi_ratio",
  "marketcap",
  "earnings_dte",
  "ask_perc",
  "bid_perc",
] as const;

export const FLOW_DEFAULTS = {
  order_by: "timestamp",
  order_dir: "desc" as "desc" | "asc",
  limit: 100,
};

export type FlowParams = Record<string, string>;

export interface ScreenerFilter {
  field: string;
  op: string;
  value: unknown;
}

export interface ScreenerBody {
  filters: ScreenerFilter[];
  select?: string[];
  order_by?: string;
  order_dir?: "asc" | "desc";
  limit: number;
}

// The columns the flow table renders. Requesting them explicitly makes REST
// rows carry the same fields the live-stream rows do (bars need bid/ask/side).
export const FLOW_DISPLAY_SELECT = [
  "ticker",
  "type",
  "strike",
  "expiry",
  "dte",
  "diff",
  "price",
  "bid",
  "ask",
  "spread",
  "underlying_price",
  "side",
  "sentiment",
  "size",
  "premium",
  "volume",
  "open_interest",
  "delta",
  "gamma",
  "theta",
  "iv",
  "vol_oi_ratio",
  "ask_perc",
  "bid_perc",
  "earnings_dte",
  "marketcap",
  "tags",
  "is_sweep",
  "is_floor",
  "is_cross",
  "sector",
  "industry",
  "exchange",
  "option_contract_ticker",
  "timestamp",
  "vega",
  "rho",
  "theo",
  "full_name",
  "canceled",
  "unusual",
  "trade_codes",
  "is_multi_leg",
  "skew",
];

/** Read a plain object of query params (from Next searchParams or URLSearchParams). */
export function toParamObject(
  sp: URLSearchParams | Record<string, string | string[] | undefined>
): FlowParams {
  const out: FlowParams = {};
  if (sp instanceof URLSearchParams) {
    sp.forEach((v, k) => {
      if (v !== "") out[k] = v;
    });
  } else {
    for (const [k, v] of Object.entries(sp)) {
      if (v == null) continue;
      const val = Array.isArray(v) ? v.join(",") : v;
      if (val !== "") out[k] = val;
    }
  }
  return out;
}

// ── ET clock helpers (the tape's native timezone) ────────────────────────────

/** Current UTC offset of America/New_York in hours (-4 EDT / -5 EST). */
function etOffsetHours(d: Date): number {
  const etH = Number(
    new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York",
      hour12: false,
      hour: "2-digit",
    }).format(d)
  );
  let diff = etH - d.getUTCHours();
  if (diff > 12) diff -= 24;
  if (diff < -12) diff += 24;
  return diff;
}

/** "HH:MM" (Eastern, today) → UTC "YYYY-MM-DD HH:MM" string for the screener. */
export function etTimeToUtcString(hhmm: string): string | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim());
  if (!m) return null;
  const now = new Date();
  const etDate = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now); // YYYY-MM-DD
  const off = etOffsetHours(now);
  const sign = off < 0 ? "-" : "+";
  const offStr = `${sign}${String(Math.abs(off)).padStart(2, "0")}:00`;
  const utcMs = Date.parse(`${etDate}T${m[1].padStart(2, "0")}:${m[2]}:00${offStr}`);
  if (!Number.isFinite(utcMs)) return null;
  const d = new Date(utcMs);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
}

/** Convert page params → the options_flow_trades screener request body. */
export function paramsToScreenerBody(params: FlowParams): ScreenerBody {
  const filters: ScreenerFilter[] = [];

  for (const field of FLOW_EQ_FIELDS) {
    const v = params[field];
    if (!v) continue;
    if (field === "ticker") {
      // Comma list → in_range (multi-ticker), single → equal. Always upper.
      const list = v.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean);
      if (list.length > 1) filters.push({ field, op: "in_range", value: list });
      else if (list.length === 1) filters.push({ field, op: "equal", value: list[0] });
    } else {
      // type/side/sentiment are lowercase enums (call, ask, bullish, ...).
      filters.push({ field, op: "equal", value: v.toLowerCase() });
    }
  }

  for (const field of FLOW_NUMERIC_FIELDS) {
    const min = params[`min_${field}`];
    const max = params[`max_${field}`];
    if (min != null && min !== "") {
      filters.push({ field, op: "greater_or_equal", value: Number(min) });
    }
    if (max != null && max !== "") {
      filters.push({ field, op: "less_or_equal", value: Number(max) });
    }
  }

  for (const [param, field] of Object.entries(FLOW_MULTI_FIELDS)) {
    const v = params[param];
    if (v) {
      const list = v
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
        .map((s) => (param === "sides" ? s.toLowerCase() : s));
      if (list.length) filters.push({ field, op: "in_range", value: list });
    }
  }

  for (const field of FLOW_BOOL_FIELDS) {
    const v = params[field];
    if (v == null || v === "") continue;
    const s = String(v).toLowerCase();
    if (["1", "true", "yes"].includes(s)) {
      filters.push({ field, op: "equal", value: true });
    } else if (["0", "false", "no"].includes(s)) {
      // Hide semantics: e.g. is_sweep=false → drop sweeps.
      filters.push({ field, op: "equal", value: false });
    }
  }

  // Flag-type allow-list. sweep/cross/floor exclusions map to equal:false
  // filters. When exactly ONE special flag is selected (e.g. "Floor only"),
  // ALSO push the positive equal:true — otherwise the server returns the
  // newest page (mostly normal prints) and client filtering leaves ~nothing
  // even though matching prints exist deeper in the tape. Multi-flag picks
  // (e.g. sweeps+floor) can't be OR'd server-side and stay client-enforced.
  const flagTypes = parseCsvSet(params.flag_types);
  if (flagTypes && flagTypes.size > 0) {
    const special = [
      ["sweep", "is_sweep"],
      ["cross", "is_cross"],
      ["floor", "is_floor"],
    ] as const;
    for (const [flag, field] of special) {
      if (!flagTypes.has(flag)) filters.push({ field, op: "equal", value: false });
    }
    if (!flagTypes.has("normal") && flagTypes.size === 1) {
      for (const [flag, field] of special) {
        if (flagTypes.has(flag)) filters.push({ field, op: "equal", value: true });
      }
    }
  }

  // Fill-side allow-list pushdown. "none" means the side column is empty,
  // which can't be filtered server-side — so only push when it's not picked.
  // The client-side allow-list still runs (needed for "none" + live rows).
  const sideSet = parseCsvSet(params.sides);
  if (sideSet && sideSet.size > 0 && sideSet.size < 4 && !sideSet.has("none")) {
    filters.push({ field: "side", op: "in_range", value: Array.from(sideSet) });
  }

  // Earnings-gap prefilter: the exact gap math (earnings_dte − dte) runs
  // client-side, but rows WITHOUT an earnings date can never match a gap
  // range — and they dominate the newest page (index/ETF flow). Requiring
  // earnings_dte server-side fills the page with candidate rows instead of
  // filtering ~1 usable row out of 500.
  const hasGapFilter =
    (params.min_earnings_gap != null && params.min_earnings_gap !== "") ||
    (params.max_earnings_gap != null && params.max_earnings_gap !== "");
  if (hasGapFilter) {
    filters.push({ field: "earnings_dte", op: "greater_or_equal", value: 0 });
  }

  // Earnings-week pushdown: earnings_dte is a real screener field, so this
  // MUST filter server-side. As a client-only filter it silently returned
  // nothing: the server sends the newest N prints market-wide (mostly index/
  // ETF flow with no earnings date), and filtering that page in the browser
  // leaves 0 rows even though matching trades exist deeper in the tape.
  if (["1", "true", "yes"].includes(String(params.earnings_week || "").toLowerCase())) {
    filters.push({ field: "earnings_dte", op: "greater_or_equal", value: 0 });
    filters.push({ field: "earnings_dte", op: "less_or_equal", value: 7 });
  }

  // Time-of-day window (Eastern) → UTC timestamp bounds.
  if (params.min_time) {
    const utc = etTimeToUtcString(params.min_time);
    if (utc) filters.push({ field: "timestamp", op: "greater_or_equal", value: utc });
  }
  if (params.max_time) {
    const utc = etTimeToUtcString(params.max_time);
    if (utc) filters.push({ field: "timestamp", op: "less_or_equal", value: utc });
  }

  const order_by = FLOW_ORDER_ALLOWED.includes(
    (params.order_by || "") as (typeof FLOW_ORDER_ALLOWED)[number]
  )
    ? params.order_by
    : FLOW_DEFAULTS.order_by;
  const order_dir = params.order_dir === "asc" ? "asc" : FLOW_DEFAULTS.order_dir;
  const limit = Math.max(
    1,
    Math.min(Number(params.limit) || FLOW_DEFAULTS.limit, 500)
  );

  return {
    filters,
    select: FLOW_DISPLAY_SELECT,
    order_by,
    order_dir,
    limit,
  };
}

/** Build the SSE query string (params passed straight to the hub predicate). */
export function paramsToStreamQuery(params: FlowParams): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (k === "limit" || k === "order_by" || k === "order_dir") continue;
    if (v !== "") sp.set(k, v);
  }
  return sp.toString();
}

/** Count of active *filter* params (ignores sort/limit) for the badge. */
export function countActiveFilters(params: FlowParams): number {
  let n = 0;
  const ignore = new Set(["order_by", "order_dir", "limit"]);
  for (const k of Object.keys(params)) {
    if (!ignore.has(k) && params[k] !== "") n += 1;
  }
  return n;
}

// ── Client-side row filters ───────────────────────────────────────────────────
// Applied in the browser to BOTH the REST result and every live row, covering
// the handful of semantics the screener/hub can't express server-side.

type Row = Record<string, unknown>;

function parseCsvSet(v: string | undefined): Set<string> | null {
  if (!v) return null;
  const set = new Set(
    v.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean)
  );
  return set.size ? set : null;
}

function rowNum(r: Row, k: string): number | null {
  const v = r[k];
  if (v == null || v === "") return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

function isTrue(v: string | undefined): boolean {
  return v != null && ["1", "true", "yes"].includes(String(v).toLowerCase());
}

export function rowPassesClientFilters(r: Row, params: FlowParams): boolean {
  // Canceled/modified prints hidden unless explicitly shown (live rows only —
  // REST rows never carry the flag).
  if (!isTrue(params.show_canceled) && r.canceled === true) return false;

  // Fill-side allow-list including "none" (prints with no detectable side).
  const sides = parseCsvSet(params.sides);
  if (sides && sides.size > 0 && sides.size < 4) {
    const s = String(r.side || "none").toLowerCase();
    if (!sides.has(s)) return false;
  }

  // Flag-type allow-list, including the "normal" (unflagged) category.
  const flagTypes = parseCsvSet(params.flag_types);
  if (flagTypes && flagTypes.size > 0 && flagTypes.size < 4) {
    const cat = r.is_sweep
      ? "sweep"
      : r.is_cross
        ? "cross"
        : r.is_floor
          ? "floor"
          : "normal";
    if (!flagTypes.has(cat)) return false;
  }

  // Earnings mechanics. gap = days between contract expiry and next earnings
  // (positive → earnings after expiry). Rows with no earnings date pass the
  // gap range only if no min/max set; avoid_earnings treats them as safe.
  const dte = rowNum(r, "dte");
  const earnDte = rowNum(r, "earnings_dte");
  const minGap = params.min_earnings_gap !== undefined && params.min_earnings_gap !== "" ? Number(params.min_earnings_gap) : null;
  const maxGap = params.max_earnings_gap !== undefined && params.max_earnings_gap !== "" ? Number(params.max_earnings_gap) : null;
  if (minGap != null || maxGap != null) {
    if (earnDte == null || dte == null) return false;
    const gap = earnDte - dte;
    if (minGap != null && gap < minGap) return false;
    if (maxGap != null && gap > maxGap) return false;
  }
  if (isTrue(params.avoid_earnings)) {
    // Keep contracts that expire before the earnings date (or have none).
    if (earnDte != null && dte != null && earnDte <= dte) return false;
  }
  if (isTrue(params.earnings_week)) {
    if (earnDte == null || earnDte > 7) return false;
  }

  return true;
}

export function applyClientFilters(rows: Row[], params: FlowParams): Row[] {
  const needed =
    !isTrue(params.show_canceled) ||
    params.flag_types ||
    params.sides ||
    params.min_earnings_gap ||
    params.max_earnings_gap ||
    isTrue(params.avoid_earnings) ||
    isTrue(params.earnings_week);
  if (!needed) return rows;
  return rows.filter((r) => rowPassesClientFilters(r, params));
}

/** Collapse identical prints (same contract + fill + side) into one row. */
export function aggregateRows(rows: Row[]): Row[] {
  const out: Row[] = [];
  const idx = new Map<string, number>();
  for (const r of rows) {
    const key = `${r.option_contract_ticker || `${r.ticker}|${r.strike}|${r.type}|${r.expiry}`}|${r.price}|${r.side}`;
    const at = idx.get(key);
    if (at == null) {
      idx.set(key, out.length);
      out.push({ ...r, agg_count: 1 });
    } else {
      const base = out[at];
      base.size = (Number(base.size) || 0) + (Number(r.size) || 0);
      base.premium = (Number(base.premium) || 0) + (Number(r.premium) || 0);
      base.agg_count = (Number(base.agg_count) || 1) + 1;
    }
  }
  return out;
}

// ── Filter param → table column mapping (drives the apply-pulse animation) ───
export const PARAM_TO_COLUMN: Record<string, string[]> = {
  ticker: ["ticker"],
  type: ["contract"],
  side: ["side"],
  sides: ["side"],
  sentiment: ["tagscol"],
  premium: ["premium"],
  size: ["size"],
  price: ["spot"],
  spread: ["spreadval"],
  underlying_price: ["stock"],
  strike: ["contract"],
  dte: ["dte"],
  expiry_dates: ["expiry"],
  opex_only: ["expiry"],
  hide_expired: ["expiry"],
  delta: ["delta"],
  gamma: ["gamma"],
  theta: ["theta"],
  iv: ["iv"],
  volume: ["volume"],
  open_interest: ["oi"],
  vol_oi_ratio: ["voloi"],
  vol_greater_oi: ["voloi"],
  size_greater_oi: ["voloi", "size"],
  unusual: ["flags"],
  diff: ["diff"],
  is_otm: ["moneyness", "diff"],
  exclude_deep_itm: ["moneyness", "diff"],
  ask_perc: ["chain"],
  bid_perc: ["chain"],
  earnings_dte: ["earnings_dte"],
  min_earnings_gap: ["earnings_dte"],
  max_earnings_gap: ["earnings_dte"],
  avoid_earnings: ["earnings_dte"],
  earnings_week: ["earnings_dte"],
  marketcap: ["marketcap"],
  sectors: ["sector"],
  industries: ["industry"],
  issue_types: ["ticker"],
  exchanges: ["exchange"],
  flag_types: ["flags"],
  is_sweep: ["flags"],
  is_floor: ["flags"],
  is_cross: ["flags"],
  is_multi_leg: ["legs"],
  is_single_leg: ["legs"],
  tags: ["tagscol"],
  excluded_tags: ["tagscol"],
  min_time: ["timestamp"],
  max_time: ["timestamp"],
  show_canceled: ["flags"],
  trade_codes: ["code"],
  skew: ["chain"],
};

/** Which table columns are touched by the params that changed between a and b. */
export function changedColumns(a: FlowParams, b: FlowParams): Set<string> {
  const cols = new Set<string>();
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const k of keys) {
    if ((a[k] || "") === (b[k] || "")) continue;
    const base = k.replace(/^min_|^max_/, "");
    const mapped = PARAM_TO_COLUMN[k] || PARAM_TO_COLUMN[base];
    if (mapped) mapped.forEach((c) => cols.add(c));
  }
  return cols;
}
